import { TenantContract, CreateContractResponse, ListContractsResponse, ResendLinkResponse } from '@/types';


const API_BASE_URL = (import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_URL || 'https://api.quadradois.com.br'));

class TenantContractsService {
    private getToken(): string | null {
        return localStorage.getItem('token');
    }

    private async fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const token = this.getToken();

        if (!token) {
            throw new Error('Token de autenticação não encontrado');
        }

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers as any, // Cast to any to avoid type issues with HeadersInit overlap
        };

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
        });

        // Handle PDF download explicitly
        if (headers['Accept' as keyof HeadersInit] === 'application/pdf') {
            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorData = JSON.parse(errorText);
                    throw new Error(errorData.message || `Erro ${response.status}`);
                } catch {
                    throw new Error(errorText || `Erro ${response.status}`);
                }
            }
            return response as unknown as T;
        }

        const data = await response.json();

        if (!response.ok) {
            const error = data as { message?: string; error?: string };
            throw new Error(error.message || error.error || `Erro ${response.status}`);
        }

        return data as T;
    }

    async listContracts(tenantId: number): Promise<TenantContract[]> {
        const response = await this.fetchWithAuth<ListContractsResponse>(
            `/api/tenants/${tenantId}/contracts`
        );
        return response.contracts;
    }

    async createContract(tenantId: number, planDetails?: { plan_name: string; monthly_price: string }): Promise<CreateContractResponse> {
        return this.fetchWithAuth<CreateContractResponse>(
            `/api/tenants/${tenantId}/contract`,
            {
                method: 'POST',
                body: planDetails ? JSON.stringify(planDetails) : undefined,
            }
        );
    }

    async resendLink(tenantId: number, contractUuid: string): Promise<ResendLinkResponse> {
        return this.fetchWithAuth<ResendLinkResponse>(
            `/api/tenants/${tenantId}/contract/${contractUuid}/resend`,
            {
                method: 'POST',
            }
        );
    }

    async downloadPdf(contractUuid: string): Promise<Blob> {
        const response = await this.fetchWithAuth<Response>(
            `/api/contracts/saas/${contractUuid}/pdf`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/pdf'
                }
            }
        );
        return response.blob();
    }

    triggerDownload(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    async downloadAndSavePdf(contractUuid: string): Promise<void> {
        const blob = await this.downloadPdf(contractUuid);
        this.triggerDownload(blob, `contrato-${contractUuid}.pdf`);
    }

    /** Cancela um contrato ativo/assinado */
    async cancelContract(tenantId: number, contractUuid: string, reason?: string): Promise<void> {
        return this.fetchWithAuth<void>(
            `/api/tenants/${tenantId}/contract/${contractUuid}/cancel`,
            {
                method: 'POST',
                body: JSON.stringify({ reason }),
            }
        );
    }

    /** Exclui um contrato pendente (rascunho) */
    async deleteContract(tenantId: number, contractUuid: string): Promise<void> {
        return this.fetchWithAuth<void>(
            `/api/tenants/${tenantId}/contract/${contractUuid}`,
            {
                method: 'DELETE',
            }
        );
    }
}

export const tenantContractsService = new TenantContractsService();
